import { ReactNode, useMemo } from 'react';

import { CalendarIcon } from '@radix-ui/react-icons';
import { Flex, Text } from '@raystack/apsara';
import { Show } from '@studio/hoc';
import { getUSTimeFormat } from '@studio/utils';

import { V1Alpha1InferenceResponseBody } from '@src/clients/dexter/api';
import { SquareThumbnail } from '@src/components/Custom/Thumbnails';
import { useGetBlockDetails } from '@src/hooks/useBlocksHooks';
import { INPUT_FORMATS } from '@src/utils/constants/WORKFLOW_CONSTANTS';

export interface InferenceAssetItem {
  src: string;
  name: string;
  children: ReactNode;
}

export const InferenceAssetItem = ({
  src,
  name,
  children
}: InferenceAssetItem) => {
  return (
    <Flex gap='small'>
      <SquareThumbnail src={src} />
      <Flex direction='column' gap='extra-small' justify='center' align='start'>
        <Text size={4} weight={500}>
          {name}
        </Text>
        {children}
      </Flex>
    </Flex>
  );
};

export const InferenceItemContainer = ({
  insight,
  subtitleOverride
}: {
  insight: V1Alpha1InferenceResponseBody & {
    block_info: { [key: string]: string };
  };
  subtitleOverride?: string;
}) => {
  const { block_info = {} } = insight || {};
  const { data } = useGetBlockDetails({
    blockName: block_info?.name ?? '',
    blockVersion: block_info?.version ?? ''
  });

  const rasterInput = insight?.input?.find(
    i => i.format === INPUT_FORMATS.FormatRaster
  );

  const { properties } = rasterInput || {};

  const thumbnailSRC = useMemo(() => data?.spec?.thumbnail_url, [data]);

  return (
    <InferenceAssetItem src={thumbnailSRC} name={insight?.name ?? ''}>
      <Flex gap='small' align='center'>
        <Show
          when={properties?.date}
          fallback={
            <Text size={2} variant='secondary'>
              {subtitleOverride}
            </Text>
          }
        >
          <CalendarIcon />

          <Text size={2} weight={400}>
            {getUSTimeFormat(properties?.date)}
          </Text>
        </Show>
      </Flex>
    </InferenceAssetItem>
  );
};
